import React, { useState, useEffect, SyntheticEvent, useMemo, useRef } from 'react';
import { Typography, TextField, Button, Box, MenuItem, Paper } from '@mui/material';
import { DateField, PageHeader } from '@routes-plan/ui';
import axios from 'axios';
import { NavLink, useNavigate } from 'react-router-dom';
import { Route } from '../../models/route';
import { Client } from '../../models/client';
import { useT } from '../../i18n';
import { LoanType } from '../../models/loan-type';
import { Office } from '../../models/office';
import useAxiosInterceptor from '../AxiosInterceptor';
import moment from 'moment-timezone';
import { useSelection } from '../../components/context/SelectionContext';
import PermissionButton from '../../components/common/PermissionButton';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { apiErrorMessage, formatMoney, newRequestId } from '../inventory/shared/inventoryApi';
import LoanProductsPicker, { LoanableProduct, PricingMode, SelectedLoanItem, itemTotalWithMargin } from './LoanProductsPicker';
import { setPageTitle } from '../../utils/pageTitle';
import { ScreenTourButton } from '../../components/help/ScreenHelp';
import { operationalTimezone } from '../../utils/operationalDateTime';
import { formActionsSx, formGridSx, formMaxWidthSx, pageSx, sectionSx } from './components/layout';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

/** Alineado con CreateLoan (API): con `allowClientCreditLimit` en la ruta, tope = cupo del cliente; si no, cupo de la ruta. */
const effectiveCreditLimit = (
  allowClientCreditLimit: boolean,
  clientCreditLimit: unknown,
  routeCreditLimit: unknown
): number => {
  const cq = Number(clientCreditLimit) || 0;
  const oq = Number(routeCreditLimit) || 0;
  return allowClientCreditLimit ? cq : oq;
};

/** Cuotas sugeridas: las del plazo de las constantes de Mis Rutas; los tipos nuevos no traen plazo. */
const suggestedInstallments = (loanType: LoanType): string => {
  const term = Number(loanType.term) || 0;
  return term > 0 ? String(getPeriodCount(term, Number(loanType.loanTypeKind?.id))) : '';
};

const getPeriodCount = (termInDays: number, loanTypeKindId?: number | null): number => {
  if (!Number.isFinite(termInDays) || termInDays <= 0) return 1;
  if (loanTypeKindId === 2) return Math.max(1, Math.round((termInDays / 30) * 4));
  if (loanTypeKindId === 3) return Math.max(1, Math.round(termInDays / 15));
  if (loanTypeKindId === 4) return Math.max(1, Math.round(termInDays / 30));
  return termInDays;
};

/** Inventario prestable de la ruta (mismo endpoint que usa la app). */
interface RouteProductInventory {
  allowProductLoans: boolean;
  allowCashLoans: boolean;
  pricingMode: PricingMode;
  products: LoanableProduct[];
}

/** Cálculo del préstamo de producto hecho por el servidor (`POST loans/preview`). */
interface LoanPreview {
  pricingMode: PricingMode;
  value: number;
  productValue: number;
  cashValue: number;
  totalValue: number;
  interestRate: number;
  termDays: number;
  installmentValue: number;
  insuranceCharge: number;
}

/** Cliente de la ruta con sus préstamos (`GET routes/:id/clients`). */
type RouteClientWithLoans = Client & {
  loans?: { id: number; position: number; state: number }[];
  /** Cliente de otra ruta de la sede (sede con clientes compartidos). */
  sharedFrom?: { id: number; name: string | null } | null;
};

const LoanNew: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  setPageTitle('pageTitle.loanNew');
  const navigate = useNavigate();
  const [clientId, setClientId] = useState<number | string>('');
  const [value, setValue] = useState("");
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  /** Número de cuotas del préstamo (se envía como `installments` y `termDays`). */
  const t = useT();
  const [termDays, setTermDays] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [position, setPosition] = useState("");
  const [creditLimit, setCreditLimit] = useState<number>(0);
  // Cupo disponible = tope − lo que el cliente ya debe (lo calcula el API con la regla con que valida al guardar).
  const [creditDetail, setCreditDetail] = useState<{ cap: number; outstanding: number } | null>(null);
  const [routeClients, setRouteClients] = useState<Client[]>([]);
  const [positions, setPositions] = useState<RouteClientWithLoans[]>([]);

  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [insuranceCharge, setInsuranceCharge] = useState<number>(0);
  const [insuranceChargeFocused, setInsuranceChargeFocused] = useState(false);
  const [loanTypeId, setLoanTypeId] = useState('');
  const [totalValue, setTotalValue] = useState<number>(0);
  const [installmentValue, setInstallmentValue] = useState<number>(0);
  const [location, setLocation] = useState<string | null>(null);
  const { office_id: officeId, setOffice_id: setOfficeId, collect_id: routeId, setCollect_id: setRouteId, user_id: userId, setUser_id: setUserId } = useSelection();
  const { canCreate, canAllowHistoricalLendDate } = useResourcePermissions('/loans/payments');
  const [isWeekly, setIsWeekly] = useState(false);
  const [selectedLoanTypeKindId, setSelectedLoanTypeKindId] = useState<number | null>(null);
  const [visitDay, setVisitDay] = useState<string>('');
  const todayDate = moment().tz(operationalTimezone()).format('YYYY-MM-DD');
  const allowedOfficeIds = useMemo(
    () => new Set(offices.map((office) => Number(office.id))),
    [offices]
  );
  const prevRouteIdForInsurance = useRef<string | number | null>(null);

  // Préstamo de producto (rutas de sedes de objeto real).
  const [productInventory, setProductInventory] = useState<RouteProductInventory | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loanItems, setLoanItems] = useState<SelectedLoanItem[]>([]);
  const [cashValue, setCashValue] = useState('');
  const [preview, setPreview] = useState<LoanPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const requestId = useRef(newRequestId('lend'));
  const previewSeq = useRef(0);
  const productMode = Boolean(productInventory?.allowProductLoans);
  const pricingMode: PricingMode = productInventory?.pricingMode === 'PRODUCT' ? 'PRODUCT' : 'CONSTANT';
  const allowCash = productInventory ? productInventory.allowCashLoans : true;
  const selectedItems = useMemo(() => loanItems.filter((item) => item.quantity > 0), [loanItems]);
  // Valor que se muestra: con utilidad del producto, los precios ya llevan su % (se guarda igual que antes).
  const displayLoanValue = useMemo(() => {
    if (!preview) return null;
    if (preview.pricingMode !== 'PRODUCT') return preview.value;
    return selectedItems.reduce((sum, item) => sum + itemTotalWithMargin(item), 0) + (Number(preview.cashValue) || 0);
  }, [preview, selectedItems]);

  const [currentDate, setCurrentDate] = useState(() => {
    return moment().tz(operationalTimezone()).format('YYYY-MM-DD'); // valor inicial: hoy
  });

  useEffect(() => {
    if (!canAllowHistoricalLendDate && currentDate < todayDate) {
      setCurrentDate(todayDate);
    }
  }, [canAllowHistoricalLendDate, currentDate, todayDate]);

  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const [
          { data: userData },
          { data: routesData },
          { data: officesData },
          { data: clientsData },
        ] = await Promise.all([
          axios.get("user"),
          axios.get("routes"),
          axios.get("offices"),
          axios.get("clients"),
        ]);
        const officePayload = Array.isArray(officesData?.data)
          ? officesData.data
          : Array.isArray(officesData)
            ? officesData
            : [];
        setUserId(userData.data.id);
        setRoutes(routesData.data);
        setOffices(officePayload);
        setClients(clientsData.data);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos
      } catch (error) {
        showAlert(t('common.loadError'), 'error');
      } finally {
        hideLoading();
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    const fetchCoordinates = async () => {
      try {
        const coords = await getCoordinates();
        setLocation(coords);
      } catch (error) {
        showAlert(t('loans.gpsRequiredLoad'), 'error');
      }
    };

    fetchCoordinates();
  }, []); // solo al montar el componente

  useEffect(() => {
    if (officeId && allowedOfficeIds.has(Number(officeId))) {
      const fetchLoanTypes = async () => {
        showLoading();
        try {
          const { data } = await axios.get(`offices/${officeId}/loan-types/complete`);
          setLoanTypes(data.loanTypes)
        } catch (error) {
          showAlert(t('loans.noLoanTypesForOffice'), 'warning');
        } finally {
          hideLoading();
        }
      };

      fetchLoanTypes();
    } else {
      setLoanTypes([]);
      setLoanTypeId('');
    }
  }, [officeId, allowedOfficeIds]);

  useEffect(() => {
    const routeAllowed = officeRoutes.some((route: Route) => route.id === Number(routeId));
    if (routeId && routeAllowed) {
      showLoading();
      (async () => {
        try {
          const { data } = await axios.get(`routes/${routeId}/clients`);
          setRouteClients(data.data);
          setPositions(data.data);
          //console.log("Collect Client Data:", data.data);
          if (data.data.length === 0) {
            showAlert(t('loans.noClientsInRoute'), 'warning');
          }
        } catch (error) {
          showAlert(t('common.loadError'), 'error');
        } finally {
          hideLoading();
        }
      })();
    } else {
      setRouteClients([]);
      setPositions([]);
    }
  }, [routeId, officeRoutes]);

  useEffect(() => {
    if (officeId && allowedOfficeIds.has(Number(officeId))) {
      (async () => {
        try {
          const { data } = await axios.get(`offices/${officeId}/routes`);
          setOfficeRoutes(data);
          if (!data.some((route: Route) => route.id === Number(routeId))) {
            setRouteId(""); // Reset routeId if it's not in the new list
          }
        } catch (error) {
          showAlert(t('common.loadError'), 'error');
        }
      })();
    } else {
      setOfficeRoutes([]);
      setRouteId("");
    }
  }, [officeId, allowedOfficeIds]);

  useEffect(() => {
    const selectedRoute = officeRoutes.find((route: Route) => route.id === Number(routeId));
    const routeCreditLimit = selectedRoute ? Number(selectedRoute.creditLimit) || 0 : 0;
    const allowClientCreditLimit = Boolean(
      Number((selectedRoute as Route | undefined)?.allowClientCreditLimit ?? 0)
    );
    setCreditDetail(null);
    if (!routeId || !selectedRoute) {
      setCreditLimit(0);
      return;
    }
    if (!clientId) {
      setCreditLimit(routeCreditLimit);
      return;
    }
    const selectedClient = routeClients.find((c: Client) => Number(c.id) === Number(clientId));
    if (!selectedClient) {
      setCreditLimit(routeCreditLimit);
      return;
    }
    // Mientras responde el API, el tope sin descontar la deuda; el API vuelve a validar al guardar.
    setCreditLimit(effectiveCreditLimit(allowClientCreditLimit, selectedClient.creditLimit, routeCreditLimit));
    let alive = true;
    axios
      .get(`clients/${clientId}/available-credit`, { params: { routeId } })
      .then(({ data }) => {
        if (!alive || !data?.data) return;
        setCreditLimit(Number(data.data.available) || 0);
        setCreditDetail({ cap: Number(data.data.cap) || 0, outstanding: Number(data.data.outstanding) || 0 });
      })
      .catch(() => {
        if (alive) showAlert(t('loans.creditCheckError'), 'warning');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, officeRoutes, clientId, routeClients]);

  useEffect(() => {
    const selectedRoute = officeRoutes.find((route: Route) => route.id === Number(routeId));
    const requestedValue = parseFloat(value) || 0;
    const routeChanged = String(routeId) !== String(prevRouteIdForInsurance.current);
    prevRouteIdForInsurance.current = routeId;

    if (!selectedRoute) {
      setInsuranceCharge(0);
      return;
    }

    if (routeChanged) {
      setInsuranceCharge(0);
    }

    // En préstamo de producto el gasto automático lo calcula el servidor (vista previa).
    if (productMode) {
      return;
    }

    if (selectedRoute.manualInsurance) {
      if (requestedValue <= 0) {
        setInsuranceCharge(0);
      }
      return;
    }

    if (requestedValue <= 0) {
      setInsuranceCharge(0);
      return;
    }

    const insuranceValue = Number(selectedRoute.insurance ?? 0);
    const consInsuranceValue = Number(selectedRoute.insuranceRate ?? 0);

    if (insuranceValue <= 0 || consInsuranceValue <= 0) {
      setInsuranceCharge(0);
      return;
    }

    setInsuranceCharge(Math.ceil((requestedValue / consInsuranceValue) * insuranceValue));
  }, [routeId, officeRoutes, value, productMode]);

  useEffect(() => {
    if (productMode) return; // valores de la vista previa del servidor
    const parsedValue = parseFloat(value) || 0;
    const parsedInterestRate = parseFloat(interestRate) || 0;
    const periodCount = Math.max(1, parseInt(termDays) || 1);

    const totalValue = parsedValue + (parsedValue * parsedInterestRate / 100);
    const dailyPayValue = Math.round(totalValue / periodCount);

    setTotalValue(totalValue);
    setInstallmentValue(dailyPayValue);
  }, [value, termDays, interestRate, selectedLoanTypeKindId, productMode]);

  const loadProductInventory = async (inventoryRouteId: string | number) => {
    setLoadingProducts(true);
    try {
      const { data } = await axios.get(`mobile/inventory/${inventoryRouteId}`);
      setProductInventory({
        allowProductLoans: Boolean(data?.allowProductLoans),
        allowCashLoans:
          data?.businessModel?.allowCashLoans === undefined
            ? !data?.allowProductLoans
            : Boolean(data.businessModel.allowCashLoans),
        pricingMode: data?.pricingMode === 'PRODUCT' ? 'PRODUCT' : 'CONSTANT',
        products: (Array.isArray(data?.products) ? data.products : []).map((p: any) => ({
          productId: Number(p.productId),
          title: String(p.title ?? ''),
          sku: p.sku ?? null,
          price: Number(p.price) || 0,
          marginPercent: p.marginPercent ?? null,
          available: Number(p.available) || 0,
        })),
      });
    } catch (error) {
      // Sin inventario (API sin el módulo o sin acceso): el préstamo sigue en efectivo.
      setProductInventory(null);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Al cambiar de ruta se recarga su inventario y se limpia lo seleccionado.
  useEffect(() => {
    setLoanItems([]);
    setCashValue('');
    setPreview(null);
    setPreviewError(null);
    const routeAllowed = officeRoutes.some((route: Route) => route.id === Number(routeId));
    if (routeId && routeAllowed) {
      loadProductInventory(routeId);
    } else {
      setProductInventory(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, officeRoutes]);

  // Si se actualiza el disponible, las cantidades no pueden superarlo.
  useEffect(() => {
    if (!productInventory) return;
    setLoanItems((prev) =>
      prev
        .map((item) => {
          const available = productInventory.products.find((p) => p.productId === item.productId)?.available ?? 0;
          return { ...item, quantity: Math.min(item.quantity, available) };
        })
        .filter((item) => productInventory.products.some((p) => p.productId === item.productId && p.available > 0)),
    );
  }, [productInventory]);

  const manualInsurance = Boolean(
    officeRoutes.find((route: Route) => route.id === Number(routeId))?.manualInsurance
  );

  // Vista previa del servidor: mismo cálculo que guardará el préstamo (modo de la ruta, utilidad, gasto).
  useEffect(() => {
    if (!productMode) return;
    const cash = allowCash ? Number(cashValue) || 0 : 0;
    if (!loanTypeId || selectedItems.length === 0) {
      setPreview(null);
      setPreviewError(null);
      setTotalValue(0);
      setInstallmentValue(0);
      if (!manualInsurance) setInsuranceCharge(0);
      return;
    }
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const { data } = await axios.post('loans/preview', {
          routeId,
          ...(clientId ? { clientId } : {}),
          loanTypeId,
          items: selectedItems.map((item) => ({ productId: item.productId, quantity: item.quantity })),
          cashValue: cash,
          ...(termDays ? { installments: Number(termDays) } : {}),
          ...(!fixedInterest && interestRate !== '' ? { interestRate: Number(interestRate) } : {}),
          ...(manualInsurance ? { insuranceCharge } : {}),
        });
        if (seq !== previewSeq.current) return;
        setPreview(data);
        setPreviewError(null);
        setTotalValue(Number(data.totalValue) || 0);
        setInstallmentValue(Number(data.installmentValue) || 0);
        setInterestRate(String(data.interestRate ?? ''));
        if (!manualInsurance) setInsuranceCharge(Number(data.insuranceCharge) || 0);
      } catch (error) {
        if (seq !== previewSeq.current) return;
        setPreview(null);
        setPreviewError(apiErrorMessage(error, t('loans.previewError')));
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productMode, selectedItems, cashValue, loanTypeId, routeId, clientId, allowCash, manualInsurance, manualInsurance ? insuranceCharge : null, termDays, interestRate]);

  const paymentCount = parseInt(termDays) || 0;
  const selectedLoanType = loanTypes.find((c: LoanType) => String(c.id) === String(loanTypeId));
  const fixedInterest = !selectedLoanType || Number(selectedLoanType.fixedInterest ?? 1) !== 0;


  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        showAlert(t('loans.geoUnsupported'), 'warning');
        return reject(new Error('Geolocation not supported'));
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const coordinates = `${lat}, ${lng}`;
          resolve(coordinates);
        },
        (error) => {
          showAlert(t('loans.locationError', { message: error.message }), 'error');
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,         // ⏱️ máximo 5 segundos
          maximumAge: 0          // no usar ubicación cacheada
        }
      );
    });
  };



  const submit = async (event: SyntheticEvent) => {
    event.preventDefault(); // Evita el envío del formulario por defecto
    if (!canCreate) {
      showAlert(t('loans.noPermissionCreate'), 'error');
      return;
    }
    if (!location) {
      showAlert(t('loans.gpsRequired'), 'error');
      return;
    }
    if (!routeId || !clientId || !loanTypeId || !position || !currentDate || (!productMode && !value)) {
      showAlert(!loanTypeId ? t('lend.typeRequired') : t('loans.missingFields'), 'warning');
      return;
    }
    if (!Number.isInteger(Number(termDays)) || Number(termDays) < 1) {
      showAlert(t('lend.installmentsRequired'), 'warning');
      return;
    }
    if (!fixedInterest && !(productMode && pricingMode === 'PRODUCT') && (interestRate === '' || !Number.isInteger(Number(interestRate)) || Number(interestRate) < 0)) {
      showAlert(t('lend.utilityRequired'), 'warning');
      return;
    }
    if (productMode) {
      if (selectedItems.length === 0) {
        showAlert(t('loans.addProductRequired'), 'warning');
        return;
      }
      if (previewError) {
        showAlert(previewError, 'error');
        return;
      }
      if (!preview || previewing) {
        showAlert(t('loans.waitPreview'), 'info');
        return;
      }
      // Cupo: con constante contra el valor; con utilidad del producto contra el total a pagar.
      const quotaValue = preview.pricingMode === 'PRODUCT' ? preview.totalValue : preview.value;
      if (quotaValue > creditLimit) {
        showAlert(
          preview.pricingMode === 'PRODUCT'
            ? t('loans.totalOverQuota')
            : t('loans.valueOverQuota'),
          'error',
        );
        return;
      }
    } else if (parseFloat(value) > creditLimit) {
      showAlert(t('loans.requestedOverQuota'), 'error');
      return;
    }
    if (currentDate > todayDate) {
      showAlert(t('loans.futureDate'), 'error');
      return;
    }
    if (!canAllowHistoricalLendDate && currentDate < todayDate) {
      showAlert(t('loans.pastDateNoPermission'), 'error');
      return;
    }
    if (isWeekly && (visitDay === '' || visitDay === undefined)) {
      showAlert(t('loans.selectRouteDayRequired'), 'warning');
      return;
    }

    if (saving) return;
    const formData = {
      routeId,
      clientId,
      loanTypeId,
      value: productMode && preview ? preview.value : value,
      interestRate,
      userId,
      termDays: paymentCount,
      // El API calcula total, cuota y gasto con estas cuotas (y el % si el tipo no lo tiene fijo).
      installments: paymentCount,
      position,
      date: currentDate,
      totalValue,
      installmentValue,
      insuranceCharge,
      location,
      visitDay: isWeekly ? Number(visitDay) : null,
      // Préstamo de producto: el servidor recalcula valores y descuenta el inventario de la ruta.
      ...(productMode
        ? {
            items: selectedItems.map((item) => ({ productId: item.productId, quantity: item.quantity })),
            cashValue: allowCash ? Number(cashValue) || 0 : 0,
          }
        : {}),
      clientRequestId: requestId.current,
    };
    const url = "/loans";
    try {
      setSaving(true);
      showLoading();
      await axios.post(url, formData);
      requestId.current = newRequestId('lend');
      showAlert(t('loans.createdOk'), 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));
      navigate("/loans/payments");
    } catch (error) {
      const body = (error as any)?.response?.data;
      if (body?.code === 'SAME_DAY_PAYOFF') {
        // Regla de la ruta: el cliente pagó hoy N cuotas o más; se le puede prestar desde mañana.
        showAlert(t('loans.sameDayPayoffBlocked', { installments: body.installments, loanId: body.loanId }), 'warning');
      } else if (body?.code === 'OVER_CREDIT_LIMIT') {
        // El API descuenta lo que el cliente ya debe: el cupo pudo cambiar después de abrir el formulario.
        setCreditLimit(Number(body.creditLimit) || 0);
        showAlert(t('loans.overAvailableQuota', { value: formatMoney(Number(body.creditLimit) || 0) }), 'error');
      } else {
        showAlert(apiErrorMessage(error, t('loans.createError')), 'error');
      }
      if (productMode && routeId) {
        loadProductInventory(routeId); // el disponible pudo cambiar
      }
    } finally {
      setSaving(false);
      hideLoading();
    }
  };

  const handleInterestRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (fixedInterest) return;
    const next = event.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setInterestRate(next);
  };
  const loanPositions = useMemo(
    () =>
      positions
        .flatMap((client) => {
          if (!client.loans || client.loans.length === 0) {
            return [];
          }
          return client.loans
            .filter((loan) => loan.state === 0 && loan.position !== null && loan.position !== undefined)
            .map((loan) => ({
              key: `${client.id}-${loan.id}`,
              position: loan.position,
              clientName: client.fullName,
            }));
        })
        .sort((a, b) => a.position - b.position),
    [positions]
  );

  const positionOptions = useMemo(
    () =>
      loanPositions.length > 0
        ? loanPositions
        : [{ key: 'default-position-1', position: 1, clientName: '' }],
    [loanPositions]
  );

  useEffect(() => {
    if (!routeId) {
      setPosition('');
      return;
    }

    const hasCurrentPosition = positionOptions.some(
      (item) => String(item.position) === String(position)
    );

    if (!position || !hasCurrentPosition) {
      setPosition(String(positionOptions[0]?.position ?? '1'));
    }
  }, [routeId, position, positionOptions]);

  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('loans.newTitle')}
        description={t('loans.newDescription')}
        breadcrumbs={[
          { label: t('menu.loans.payments'), href: '#/loans/payments' },
          { label: t('loans.newLoan') },
        ]}
        helpAction={<ScreenTourButton />}
      />
      <Box component="form" onSubmit={submit} sx={{ ...formMaxWidthSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper variant="outlined" sx={sectionSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('loans.sectionClient')}
          </Typography>
          <Box sx={formGridSx}>
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('common.office')}
              data-tour="loan-new-office"
              value={officeId}
              onChange={(event) => setOfficeId(event.target.value)}
            >
              {offices.map((office: Office) => (
                <MenuItem key={office.id} value={office.id}>
                  {office.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('common.route')}
              data-tour="loan-route"
              value={officeRoutes.length > 0 ? routeId : ''}
              onChange={(event) => setRouteId(event.target.value)}
            >
              {officeRoutes.map((route: Route) => (
                <MenuItem key={route.id} value={route.id}>
                  {route.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('loans.client')}
              data-tour="loan-client"
              value={routeClients.length > 0 ? clientId : ''}
              onChange={(event) => setClientId(event.target.value)}
              helperText={routeId && routeClients.length === 0 ? t('loans.noClientsInRoute') : undefined}
            >
              {routeClients.map((client: Client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.fullName}
                  {/* Sede con clientes compartidos: cliente creado en otra ruta. */}
                  {(client as RouteClientWithLoans).sharedFrom?.name
                    ? ` · ${t('clients.shared.from', { route: (client as RouteClientWithLoans).sharedFrom?.name ?? '' })}`
                    : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label={t('loans.availableQuota')}
              data-tour="loan-new-quota"
              value={formatMoney(Number(creditLimit) || 0)}
              InputProps={{ readOnly: true }}
              helperText={
                creditDetail && creditDetail.outstanding > 0
                  ? t('loans.availableQuotaHelp', { cap: formatMoney(creditDetail.cap), debt: formatMoney(creditDetail.outstanding) })
                  : undefined
              }
            />
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('loans.positionLabel')}
              data-tour="loan-new-position"
              helperText={t('loans.positionHelp')}
              value={position}
              onChange={(event) => setPosition(event.target.value)}
            >
              {positionOptions.map((option) => (
                <MenuItem key={option.key} value={String(option.position)}>
                  {option.clientName ? `${option.position} — ${option.clientName}` : `${option.position}`}
                </MenuItem>
              ))}
            </TextField>
            <DateField
              fullWidth
              size="small"
              label={t('loans.loanDate')}
              data-tour="loan-new-date"
              value={currentDate || null}
              onChange={(v) => setCurrentDate(v ?? '')}
              max={todayDate}
              min={canAllowHistoricalLendDate ? undefined : todayDate}
            />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={sectionSx}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('loans.sectionTerms')}
          </Typography>
          <Box sx={formGridSx}>
            <TextField
              select
              fullWidth
              size="small"
              required
              label={t('lend.type')}
              data-tour="loan-type"
              value={loanTypes.length > 0 ? loanTypeId : ''}
              helperText={loanTypes.length === 0 ? t('lend.noTypes') : undefined}
              onChange={(event) => {
                const nextLoanTypeId = event.target.value;
                setLoanTypeId(nextLoanTypeId);

                const nextLoanType = loanTypes.find(
                  (loanType: LoanType) => loanType.id === Number(nextLoanTypeId)
                );

                if (nextLoanType) {
                  setTermDays(suggestedInstallments(nextLoanType));
                  // Con % fijo se usa el del tipo; sin % fijo lo escribe quien presta.
                  setInterestRate(Number(nextLoanType.fixedInterest ?? 1) !== 0 ? String(nextLoanType.interestRate) : '');
                  setSelectedLoanTypeKindId(Number(nextLoanType.loanTypeKind.id));
                  setIsWeekly(nextLoanType.loanTypeKind.id === 2); // tipo semanal
                }
              }}
            >
              {loanTypes.map((loanType: LoanType) => (
                <MenuItem key={loanType.id} value={loanType.id}>
                  {loanType.name}
                </MenuItem>
              ))}
            </TextField>
            {isWeekly && (
              <TextField
                select
                fullWidth
                size="small"
                required
                label={t('loans.selectDay')}
                value={visitDay}
                onChange={(event) => setVisitDay(event.target.value)}
              >
                <MenuItem value="1">{t('loans.monday')}</MenuItem>
                <MenuItem value="2">{t('loans.tuesday')}</MenuItem>
                <MenuItem value="3">{t('loans.wednesday')}</MenuItem>
                <MenuItem value="4">{t('loans.thursday')}</MenuItem>
                <MenuItem value="5">{t('loans.friday')}</MenuItem>
                <MenuItem value="6">{t('loans.saturday')}</MenuItem>
                <MenuItem value="0">{t('loans.sunday')}</MenuItem>
              </TextField>
            )}
            <TextField
              fullWidth
              size="small"
              label={t('lend.installments')}
              data-tour="loan-new-installments"
              value={termDays}
              type="number"
              required
              inputProps={{ min: 1, max: 1000, step: 1, inputMode: 'numeric' }}
              onChange={(event) => setTermDays(event.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
            />
            {productMode ? (
              <>
                {allowCash && (
                  <TextField
                    fullWidth
                    size="small"
                    label={t('loans.extraCash')}
                    type="number"
                    value={cashValue}
                    inputProps={{ min: 0 }}
                    onChange={(event) => setCashValue(event.target.value)}
                  />
                )}
                <TextField
                  fullWidth
                  size="small"
                  label={t('loans.lendValueProducts')}
                  data-tour="loan-new-products-value"
                  value={displayLoanValue !== null ? formatMoney(displayLoanValue) : ''}
                  InputProps={{ readOnly: true }}
                  helperText={previewing ? t('lend.calculating') : undefined}
                />
              </>
            ) : (
              <TextField
                fullWidth
                size="small"
                label={t('loans.requestedValue')}
                data-tour="loan-value"
                type="number"
                required
                value={value}
                inputProps={{ maxLength: 10, min: 0, inputMode: 'numeric' }}
                onChange={(event) => setValue(event.target.value)}
              />
            )}
            {/* Rutas "con el precio del producto": el precio ya incluye la utilidad; no hay % que mostrar. */}
            {!(productMode && pricingMode === 'PRODUCT') && (
              <TextField
                fullWidth
                size="small"
                label={t('lend.utility')}
                data-tour="loan-new-utility"
                value={interestRate}
                type="number"
                required={!fixedInterest}
                helperText={selectedLoanType ? t(fixedInterest ? 'lend.utilityFixedHelp' : 'lend.utilityFreeHelp') : undefined}
                inputProps={{ min: 0, max: 1000, step: 1, inputMode: 'numeric', readOnly: fixedInterest }}
                onChange={handleInterestRateChange}
              />
            )}
            <TextField
              fullWidth
              size="small"
              label={t('loans.managementFee')}
              data-tour="loan-new-fee"
              type="number"
              value={manualInsurance && insuranceChargeFocused && Number(insuranceCharge ?? 0) === 0 ? '' : insuranceCharge}
              onChange={(e) => setInsuranceCharge(Number(e.target.value) || 0)}
              onFocus={() => setInsuranceChargeFocused(true)}
              onBlur={(e) => {
                setInsuranceChargeFocused(false);
                if (!e.target.value.trim()) {
                  setInsuranceCharge(0);
                }
              }}
              helperText={manualInsurance ? undefined : t('loans.managementFeeAuto')}
              inputProps={{ min: 0, readOnly: !manualInsurance }}
            />
          </Box>
          <Box
            data-tour="loan-new-totals"
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" component="div">{t('lend.totalToPay')}</Typography>
              <Typography variant="h6" component="div" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{formatMoney(Number(totalValue) || 0)}</Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" component="div">{t('lend.installmentValue')}</Typography>
              <Typography variant="h6" component="div" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{formatMoney(Number(installmentValue) || 0)}</Typography>
            </Box>
          </Box>
        </Paper>

        {productMode && (
          <Paper variant="outlined" sx={sectionSx} data-tour="loan-new-products">
            <LoanProductsPicker
              products={productInventory?.products ?? []}
              items={loanItems}
              onChange={setLoanItems}
              pricingMode={pricingMode}
              onRefresh={() => routeId && loadProductInventory(routeId)}
              disabled={loadingProducts || saving}
            />
            {previewError && (
              <Typography variant="body2" color="error" mt={1}>
                {previewError}
              </Typography>
            )}
          </Paper>
        )}

        <Box sx={formActionsSx}>
          <Button variant="outlined" component={NavLink} to="/loans/payments">
            {t('common.cancel')}
          </Button>
          <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            type="submit"
            data-tour="save"
            variant="contained"
            disabled={saving || (productMode && previewing)}
          >
            {saving ? t('loans.saving') : t('loans.saveLoan')}
          </PermissionButton>
        </Box>
      </Box>
    </Box>
  );
};

export default LoanNew;
